import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Edit, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, Link2, MoreHorizontal, Plus, User } from 'lucide-react';

// Sort icon component
const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig.key !== column) {
    return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
  }
  return sortConfig.direction === 'asc' 
    ? <ArrowUp className="h-4 w-4 ml-1" />
    : <ArrowDown className="h-4 w-4 ml-1" />;
};

const ContactsTable = ({
  contacts,
  selectedContacts,
  onSelectContact,
  onSelectAll,
  onQuickEdit,
  onCreateDeal,
  onLinkDeal,
  onViewProfile
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'company_name', direction: 'asc' });
  const allSelected = contacts.length > 0 && selectedContacts.length === contacts.length;

  // Handle sort column click
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort contacts based on current sort configuration
  const sortedContacts = useMemo(() => {
    const sorted = [...contacts].sort((a, b) => {
      let aVal, bVal;
      
      // Handle special cases for nested/computed fields
      if (sortConfig.key === 'phone') {
        aVal = a.phone || '';
        bVal = b.phone || '';
      } else if (sortConfig.key === 'email') {
        aVal = a.email || '';
        bVal = b.email || '';
      } else if (sortConfig.key === 'type') {
        const getTypeString = (contact) => {
          if (contact.is_person) return 'Person';
          if (contact.is_funder) return 'Funder';
          if (contact.is_broker) return 'Broker';
          if (contact.is_iso) return 'ISO';
          if (contact.is_merchant) return 'Merchant';
          if (contact.is_investor) return 'Investor';
          return 'zzz';
        };
        aVal = getTypeString(a);
        bVal = getTypeString(b);
      } else {
        aVal = a[sortConfig.key];
        bVal = b[sortConfig.key];
      }
      
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = String(bVal).toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [contacts, sortConfig]);

  const getTypeBadges = (contact) => {
    const types = [];
    if (contact.is_person) types.push({ label: 'Person', color: 'bg-rose-100 text-rose-800' });
    if (contact.is_funder) types.push({ label: 'Funder', color: 'bg-blue-100 text-blue-800' });
    if (contact.is_broker) types.push({ label: 'Broker', color: 'bg-green-100 text-green-800' });
    if (contact.is_iso) types.push({ label: 'ISO', color: 'bg-purple-100 text-purple-800' });
    if (contact.is_merchant) types.push({ label: 'Merchant', color: 'bg-orange-100 text-orange-800' });
    if (contact.is_investor) types.push({ label: 'Investor', color: 'bg-indigo-100 text-indigo-800' });
    return types;
  };

  const getPrimaryPhone = (contact) => {
    return contact.phone || '-';
  };

  const getPrimaryEmail = (contact) => {
    return contact.email || '-';
  };

  const getContactName = (contact) => {
    return contact.name || '-';
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-blue-50">
            <TableHead className="w-12">
              <Checkbox
                checked={allSelected}
                onCheckedChange={onSelectAll}
                data-testid="select-all-contacts"
              />
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => handleSort('shortcode')}
            >
              <div className="flex items-center">
                Shortcode
                <SortIcon column="shortcode" sortConfig={sortConfig} />
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => handleSort('company_name')}
            >
              <div className="flex items-center">
                Company / Name
                <SortIcon column="company_name" sortConfig={sortConfig} />
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => handleSort('name')}
            >
              <div className="flex items-center">
                Primary Contact
                <SortIcon column="name" sortConfig={sortConfig} />
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => handleSort('type')}
            >
              <div className="flex items-center">
                Type
                <SortIcon column="type" sortConfig={sortConfig} />
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => handleSort('phone')}
            >
              <div className="flex items-center">
                Phone
                <SortIcon column="phone" sortConfig={sortConfig} />
              </div>
            </TableHead>
            <TableHead 
              className="cursor-pointer hover:bg-gray-100 select-none"
              onClick={() => handleSort('email')}
            >
              <div className="flex items-center">
                Email
                <SortIcon column="email" sortConfig={sortConfig} />
              </div>
            </TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedContacts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                No contacts found
              </TableCell>
            </TableRow>
          ) : (
            sortedContacts.map((contact) => (
              <TableRow
                key={contact.id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => onViewProfile && onViewProfile(contact)}
                data-testid={`contact-row-${contact.id}`}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedContacts.includes(contact.id)}
                    onCheckedChange={() => onSelectContact(contact.id)}
                    data-testid={`select-contact-${contact.id}`}
                  />
                </TableCell>
                <TableCell className="font-mono text-blue-600 font-medium">
                  {contact.shortcode || '-'}
                </TableCell>
                <TableCell className="font-medium">{contact.company_name}</TableCell>
                <TableCell className="text-gray-600">{getContactName(contact)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {getTypeBadges(contact).map((type, idx) => (
                      <Badge key={idx} className={type.color}>
                        {type.label}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>{getPrimaryPhone(contact)}</TableCell>
                <TableCell>{getPrimaryEmail(contact)}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        data-testid={`contact-actions-${contact.id}`}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        onClick={() => onViewProfile && onViewProfile(contact)}
                        data-testid={`view-profile-${contact.id}`}
                      >
                        <User className="h-4 w-4 mr-2" />
                        View Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => onQuickEdit && onQuickEdit(contact)}
                        data-testid={`quick-edit-${contact.id}`}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Quick Edit
                      </DropdownMenuItem>
                      {contact.is_merchant && (
                        <>
                          <DropdownMenuItem 
                            onClick={() => onCreateDeal && onCreateDeal(contact)}
                            data-testid={`create-deal-${contact.id}`}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Deal
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => onLinkDeal && onLinkDeal(contact)}
                            data-testid={`link-deal-${contact.id}`}
                          >
                            <Link2 className="h-4 w-4 mr-2" />
                            Link Existing Deal
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default ContactsTable;
